import React, { createContext, useContext, useReducer, ReactNode } from "react";
import Price from "../models/Price";
import { Blog } from "../models/Blog";
import { FAQ } from "../models/Faq";
import { HomePage } from "../models/HomePage";
import { SubscribePage } from "../models/SubscribePage";
import { InquirePage } from "../models/InquirePage";
import { GlobalData } from "../models/GlobalData";
import { ContactFormField } from "../models/ContactFormField";
import { CarouselImages } from "../models/CarouselImages";
import Review from "../models/Review";
import { PortfolioKeyword } from "../models/PortfolioKeyword";
import { PortfolioAlbum } from "../models/PortfolioAlbum";

// Define the shape of your initial state
interface AppState {
  faqs: FAQ[] | undefined;
  about: string | undefined;
  subscribePage: SubscribePage | undefined;
  inquirePage: InquirePage | undefined;
  contactFormFields: ContactFormField[] | undefined;
  welcomeImage: any | undefined;
  homePage: HomePage | undefined;
  globalData: GlobalData | undefined;
  prices: Price[] | undefined;
  reviews: Review[] | undefined;
  blogs: Blog[] | undefined;
  carouselPortraitImages: CarouselImages | undefined;
  carouselLandscapeImages: CarouselImages | undefined;
  portfolioAlbums: PortfolioAlbum[] | undefined;
  isMobile: boolean;
  ignoreSubscribeForm: boolean;
  subscribeCompleted: boolean;
  baseApiPath: string;
  portfolioKeywords: PortfolioKeyword[] | undefined;
}

// Create the initial state
const initialState: AppState = {
  faqs: undefined,
  about: undefined,
  subscribePage: undefined,
  inquirePage: undefined,
  contactFormFields: undefined,
  welcomeImage: undefined,
  homePage: undefined,
  globalData: undefined,
  prices: undefined,
  reviews: undefined,
  blogs: undefined,
  carouselPortraitImages: undefined,
  carouselLandscapeImages: undefined,
  portfolioAlbums: undefined,
  isMobile: window.innerWidth < 768,
  ignoreSubscribeForm: false,
  subscribeCompleted: false,
  baseApiPath: "https://api.alamodeimage.com",
  portfolioKeywords: undefined,
};

// Create the context
export const DataContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Define the action types for your reducers
export type Action =
  | { type: "SET_FAQS"; payload: FAQ[] }
  | { type: "SET_ABOUT"; payload: any }
  | { type: "SET_SUBSCRIBE_PAGE"; payload: SubscribePage }
  | { type: "SET_INQUIRE_PAGE"; payload: InquirePage }
  | { type: "SET_CONTACT_FORM_FIELDS"; payload: ContactFormField[] }
  | { type: "SET_WELCOME_IMAGE"; payload: any }
  | { type: "SET_HOME_PAGE"; payload: HomePage }
  | { type: "SET_GLOBAL_DATA"; payload: GlobalData }
  | { type: "SET_PRICES"; payload: Price[] }
  | { type: "SET_REVIEWS"; payload: Review[] }
  | { type: "SET_BLOGS"; payload: Blog[] }
  | { type: "SET_CAROUSEL_PORTRAIT_IMAGES"; payload: CarouselImages }
  | { type: "SET_CAROUSEL_LANDSCAPE_IMAGES"; payload: CarouselImages }
  | { type: "SET_PORTFOLIO_ALBUMS"; payload: PortfolioAlbum[] }
  | { type: "SET_ISMOBILE"; payload: boolean }
  | { type: "SET_IGNORE_SUBSCRIBE_FORM"; payload: boolean }
  | { type: "SET_SUBSCRIBE_COMPLETED"; payload: boolean }
  | { type: "SET_PORTFOLIO_KEYWORDS"; payload: PortfolioKeyword[] };

// Define the reducer function
function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "SET_FAQS":
      return { ...state, faqs: action.payload };
    case "SET_ABOUT":
      return { ...state, about: action.payload };
    case "SET_SUBSCRIBE_PAGE":
      return { ...state, subscribePage: action.payload };
    case "SET_INQUIRE_PAGE":
      return { ...state, inquirePage: action.payload };
    case "SET_CONTACT_FORM_FIELDS":
      return { ...state, contactFormFields: action.payload };
    case "SET_WELCOME_IMAGE":
      return { ...state, welcomeImage: action.payload };
    case "SET_HOME_PAGE":
      return { ...state, homePage: action.payload };
    case "SET_GLOBAL_DATA":
      return { ...state, globalData: action.payload };
    case "SET_PRICES":
      return { ...state, prices: action.payload };
    case "SET_REVIEWS":
      return { ...state, reviews: action.payload };
    case "SET_BLOGS":
      return { ...state, blogs: action.payload };
    case "SET_CAROUSEL_PORTRAIT_IMAGES":
      return { ...state, carouselPortraitImages: action.payload };
    case "SET_CAROUSEL_LANDSCAPE_IMAGES":
      return { ...state, carouselLandscapeImages: action.payload };
    case "SET_PORTFOLIO_ALBUMS":
      return { ...state, portfolioAlbums: action.payload };
    case "SET_ISMOBILE":
      return { ...state, isMobile: action.payload };
    case "SET_IGNORE_SUBSCRIBE_FORM":
      return { ...state, ignoreSubscribeForm: action.payload };
    case "SET_SUBSCRIBE_COMPLETED":
      return { ...state, subscribeCompleted: action.payload };
    case "SET_PORTFOLIO_KEYWORDS":
      return { ...state, portfolioKeywords: action.payload };
    default:
      return state;
  }
}

// Create a data provider component
interface DataProviderProps {
  children: ReactNode; // Explicitly type children prop
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <DataContext.Provider value={{ state, dispatch }}>
      {children}
    </DataContext.Provider>
  );
};

// Create a custom hook to use the data context
export const useData = () => useContext(DataContext);
